import { ReduxStateHandler } from 'connect/state/ReduxStateHandler';
import Logger from 'utils/Logger';

const createSubHandler = (actionName, stateKey, legacyKey) =>
  new ReduxStateHandler({
    actionName,
    statePath: stateKey,
    legacyOptions: {
      legacyKey,
    },
  });

/**
 * Base class for saga handlers
 *
 * Note: Saga handlers must be registered manually in the `saga-registry` file.
 * @abstract
 */
export class SagaHandler {
  /**
   *
   * @param {object} options
   * @param {string} options.actionName - The name of the action to be dispatched.
   * @param {string} options.stateKey - The key of the state to be updated.
   * @param {{
   *   errorStateKey: string;
   *   loadingStateKey: string;
   *   valueStateKey: string;
   * }} [options.legacyOptions] - The legacy options for the handler.
   * @param {string[]} [options.dependencies] - The dependencies of the handler.
   */
  constructor({
    actionName,
    stateKey = null,
    legacyOptions = {},
    dependencies = [],
  }) {
    this._actionName = actionName;
    this._stateKey = stateKey;
    this._dependencies = dependencies;
    this._lastState = {};

    this.saga = this.saga.bind(this);
    this.selector = this.selector.bind(this);
    this.injectDependencies = this.injectDependencies.bind(this);

    if (stateKey) {
      const legacyErrorStateKey = legacyOptions.errorStateKey;
      this.error = createSubHandler(
        `${actionName}/ERROR`,
        `${stateKey}.error`,
        legacyErrorStateKey,
      );

      const legacyLoadingStateKey = legacyOptions.loadingStateKey;
      this.loading = createSubHandler(
        `${actionName}/LOADING`,
        `${stateKey}.loading`,
        legacyLoadingStateKey,
      );

      const legacyValueStateKey = legacyOptions.valueStateKey;
      this.value = createSubHandler(
        `${actionName}/VALUE`,
        `${stateKey}.value`,
        legacyValueStateKey,
      );

      this.success = createSubHandler(
        `${actionName}/SUCCESS`,
        `${stateKey}.success`,
      );

      this._stateHandler = new ReduxStateHandler({
        actionName: `${actionName}/STATE`,
        statePath: stateKey,
      });
    }
  }

  /**
   * Used to inject other saga handlers from the registry
   * This allows any handler to access other handlers
   * Without circular dependencies
   * @param {SagaHandlerRegistry} registry
   */
  injectDependencies(registry) {
    // Fixes HMR
    if (!Array.isArray(this._dependencies)) {
      Logger.error('dependencies is not an array', this._dependencies);
      this._dependencies = [];
    }

    const dependencies = this._dependencies.map((dependency) => {
      const parts = dependency.split('/');

      if (parts.length > 2) {
        const message = `Could not resolve dependency as it has more than one slash: ${dependency}`;
        Logger.error('SagaHandler', message);

        // This should never happen in production
        // as long as everyone tests their code
        throw new Error(message);
      }

      const prefix = parts.length > 1 ? parts[0] : null;
      const actionName = parts.at(-1);

      if (!prefix) {
        return {
          [actionName]: registry.getHandler(actionName),
        };
      }

      return {
        [prefix]: {
          [actionName]: registry.getHandler(dependency),
        },
      };
    });

    this._dependencies = dependencies.reduce(
      (acc, dependency) => ({
        ...acc,
        ...dependency,
      }),
      {},
    );
  }

  get actionName() {
    return this._actionName;
  }

  /**
   * Returns the current state of the handler
   * @param {any} state - The current state of the redux store
   * @returns {{
   *   error: any;
   *   loading: boolean;
   *   value: any;
   *   success: boolean;
   * }} The current state of the handler
   */
  selector(state) {
    if (!this._stateKey) {
      throw new Error('This handler does not have a state.');
    }

    // Use the sub handlers to get the values
    // so that legacy options are respected
    const newState = {
      error: this.error.selector(state),
      loading: this.loading.selector(state),
      value: this.value.selector(state),
      success: this.success.selector(state),
    };

    const hasChanged = Object.keys(newState).some(
      (key) => newState[key] !== this._lastState[key],
    );

    // However returning a new reference will trigger a re-render
    // So we keep returning the same reference if nothing has changed
    if (!hasChanged) {
      return this._lastState;
    }

    this._lastState = newState;
    return newState;
  }

  createAction(value) {
    return {
      type: this._actionName,
      payload: value,
    };
  }

  /**
   * Resets the value, loading, and error states to their initial values
   * @returns {UnknownAction} A redux action
   * @example
   * ```javascript
   * const action = handler.createResetAction();
   * action(dispatch);
   * ```
   */
  reset() {
    if (!this._stateKey) {
      throw new Error('This handler does not have a state.');
    }

    return {
      type: `${this._actionName}/STATE`,
      payload: {
        error: null,
        loading: false,
        value: null,
        success: null,
      },
    };
  }

  saga(_payload) {
    throw new Error('saga not implemented');
  }
}
